import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import { FamilyEnrollmentContext } from 'features/family-enrollment/context';
import { BaseStep } from '../BaseStep';
import { useBaseStyles, ButtonControls } from 'features/family-enrollment/steps/utils';
import { FormikTextField } from 'components/fields/FormikTextField';
import { FieldSpacer } from 'components/fields/FieldSpacer';
import { orgEnrollEmailValidator } from 'common/modules/form-validation/validators';
import { validateEmail } from 'common/api/users/email/validate';
import { hasErrorWithStatus } from 'common/api/utils/hasErrorWithStatus';
import { StatusCode } from 'common/api/config';
import { openLoader, LoadingOverlay, closeLoader } from 'components/loading-overlay/LoadingOverlay';
import { RegistrationRequest } from 'common/api/auth/models/RegistrationRequest';
import {
    buildFamilyEnrollmentSocialRegistrationChallengeUrl,
    createSocialRegistrationChallenge,
    persistFamilyEnrollmentSocialRegistrationState,
    SocialRegistrationProvider,
} from 'features/family-enrollment/socialRegistration';
import { link } from 'styles/typography';
import { ExternalLinks } from 'externalLinks';
import { logParticipantEnrollment, logAccountCreationMethodSelected } from 'config/analytics';
import { SocialLoginButtons } from 'components/social-login/SocialLoginButtons';

const getSocialProviderLabel = (provider?: 'google' | 'apple' | null) => {
    if (provider === 'apple') {
        return 'Apple';
    }
    if (provider === 'google') {
        return 'Google';
    }
    return 'social';
};

interface GetStartedFormValues {
    email: string;
}

const duplicateEmailErrorMessage = 'Looks like this email is already associated with an account.';
const getExistingSocialAccountErrorMessage = (provider?: 'google' | 'apple' | null) => {
    const providerLabel = getSocialProviderLabel(provider);

    return providerLabel === 'social'
        ? 'Looks like this social account or email is already associated with an account.'
        : `Looks like your ${providerLabel} account or email is already associated with an account.`;
};

export const GetStartedStep = () => {
    const {
        organization,
        userAccountInfo,
        updateUserAccountInfo,
        goToNextStep,
        loader,
        isEnrollmentTestVariant,
        isSocialRegistration,
        socialRegistrationError,
        setSocialRegistrationError,
        socialRegistrationExistingAccountProvider,
        setSocialRegistrationExistingAccountProvider,
        socialRegistrationProviders,
    } = useContext(FamilyEnrollmentContext);

    const baseStyles = useBaseStyles();
    const [_submitting, setSubmitting] = useState(false);
    const googleProvider = socialRegistrationProviders.find(
        ({ provider }) => provider === 'google'
    );
    const appleProvider = socialRegistrationProviders.find(({ provider }) => provider === 'apple');
    const hasAvailableSocialProviders = Boolean(googleProvider || appleProvider);

    const hasReadOnlySocialEmail =
        isSocialRegistration &&
        !userAccountInfo.emailRequired &&
        Boolean(userAccountInfo.socialEmail);

    const providerLabel = useMemo(
        () => getSocialProviderLabel(userAccountInfo.provider),
        [userAccountInfo.provider]
    );
    const existingSocialAccountErrorMessage = useMemo(
        () => getExistingSocialAccountErrorMessage(socialRegistrationExistingAccountProvider),
        [socialRegistrationExistingAccountProvider]
    );

    const initialValues: GetStartedFormValues = {
        email: hasReadOnlySocialEmail
            ? userAccountInfo.socialEmail || userAccountInfo.email
            : userAccountInfo.email || '',
    };

    const startSocialRegistration = useCallback(
        (provider: SocialRegistrationProvider, scheme?: string) => {
            setSocialRegistrationError(null);
            setSocialRegistrationExistingAccountProvider(null);

            const enrollmentCode = localStorage.getItem('RR_enrollmentCode') || '';
            const challenge = createSocialRegistrationChallenge();
            persistFamilyEnrollmentSocialRegistrationState({
                userAccountInfo,
                organization,
                enrollmentCode,
                codeVerifier: challenge.codeVerifier,
            });

            window.location.assign(
                buildFamilyEnrollmentSocialRegistrationChallengeUrl(provider, challenge, scheme)
            );
        },
        [
            organization,
            userAccountInfo,
            setSocialRegistrationError,
            setSocialRegistrationExistingAccountProvider,
        ]
    );

    const disconnectSocialRegistration = useCallback(() => {
        setSocialRegistrationError(null);
        setSocialRegistrationExistingAccountProvider(null);
        updateUserAccountInfo((prev: RegistrationRequest) => ({
            ...prev,
            registrationMethod: 'local',
            provider: undefined,
            externalRegistrationKey: undefined,
            socialEmail: undefined,
            emailRequired: false,
            firstName: '',
            lastName: '',
        }));
    }, [
        setSocialRegistrationError,
        setSocialRegistrationExistingAccountProvider,
        updateUserAccountInfo,
    ]);

    const onSubmit = async (
        values: GetStartedFormValues,
        formik: FormikHelpers<GetStartedFormValues>
    ) => {
        if (!isSocialRegistration) {
            logAccountCreationMethodSelected('email', isEnrollmentTestVariant ? 3 : 2);
        }
        setSubmitting(true);
        const email = hasReadOnlySocialEmail
            ? userAccountInfo.socialEmail || userAccountInfo.email
            : values.email;

        if (!isSocialRegistration || userAccountInfo.emailRequired) {
            openLoader(loader?.current as LoadingOverlay);
            const response = await validateEmail(email);
            closeLoader(loader?.current as LoadingOverlay);

            if (hasErrorWithStatus(response, StatusCode.Conflict)) {
                formik.setFieldError('email', duplicateEmailErrorMessage);
                setSubmitting(false);
                return;
            }
        }

        updateUserAccountInfo((currentState: RegistrationRequest) => ({
            ...currentState,
            email,
            username: email,
        }));

        logParticipantEnrollment(
            isEnrollmentTestVariant ? 3 : 2,
            'sign_in_information',
            {},
            {
                experiment_name: 'participant_enrollment_test_web',
                experiment_variant: isEnrollmentTestVariant ? 'variant' : 'control',
            }
        );

        setSocialRegistrationExistingAccountProvider(null);
        setSubmitting(false);
        goToNextStep();
    };

    return (
        <BaseStep
            contentClassName="text-md sm:text-lg font-thin"
            heading="Create Account"
            subheading={null}
        >
            <div className={baseStyles.formContainer}>
                {socialRegistrationError && (
                    <>
                        <p className="text-error text-sm pb-5">{socialRegistrationError}</p>
                        <FieldSpacer className="pb-5" />
                    </>
                )}
                {(hasAvailableSocialProviders || isSocialRegistration) && (
                    <SocialLoginButtons
                        connectedProvider={
                            isSocialRegistration ? userAccountInfo.provider ?? null : null
                        }
                        onGoogleSignIn={
                            isSocialRegistration
                                ? undefined
                                : () => {
                                      logAccountCreationMethodSelected(
                                          'google',
                                          isEnrollmentTestVariant ? 3 : 2
                                      );
                                      startSocialRegistration('google', googleProvider?.scheme);
                                  }
                        }
                        onAppleSignIn={
                            isSocialRegistration
                                ? undefined
                                : () => {
                                      logAccountCreationMethodSelected(
                                          'apple',
                                          isEnrollmentTestVariant ? 3 : 2
                                      );
                                      startSocialRegistration('apple', appleProvider?.scheme);
                                  }
                        }
                        onDisconnect={
                            isSocialRegistration ? disconnectSocialRegistration : undefined
                        }
                        showGoogle={Boolean(googleProvider)}
                        showApple={Boolean(appleProvider)}
                        googleLabel="Sign up with Google"
                        appleLabel="Sign up with Apple"
                    />
                )}
                <Formik
                    initialValues={initialValues}
                    initialErrors={
                        socialRegistrationExistingAccountProvider
                            ? { email: existingSocialAccountErrorMessage }
                            : undefined
                    }
                    initialTouched={
                        socialRegistrationExistingAccountProvider ? { email: true } : undefined
                    }
                    onSubmit={onSubmit}
                    enableReinitialize
                >
                    <GetStartedForm
                        isSocialRegistration={isSocialRegistration}
                        hasReadOnlySocialEmail={hasReadOnlySocialEmail}
                        emailRequired={userAccountInfo.emailRequired ?? false}
                        providerLabel={providerLabel}
                    />
                </Formik>
            </div>
        </BaseStep>
    );
};

const GetStartedForm = ({
    isSocialRegistration,
    hasReadOnlySocialEmail,
    emailRequired,
    providerLabel,
}: {
    isSocialRegistration: boolean;
    hasReadOnlySocialEmail: boolean;
    emailRequired: boolean;
    providerLabel: string;
}) => {
    return (
        <Form>
            <FormikTextField
                type="email"
                name="email"
                label="Email"
                validate={
                    !isSocialRegistration || emailRequired ? orgEnrollEmailValidator : undefined
                }
                disabled={hasReadOnlySocialEmail}
            />
            {hasReadOnlySocialEmail && (
                <p className="mt-1 text-xs text-grey-medium">
                    This email address came from {providerLabel}. Disconnect {providerLabel} to
                    choose a different email address.
                </p>
            )}
            {isSocialRegistration && emailRequired && (
                <p className="mt-1 text-xs text-grey-medium">
                    We could not get an email from {providerLabel}. Please provide the email address
                    you want to use to log in.
                </p>
            )}
            <div className="mt-6 text-xs text-grey-2">
                By clicking <em>Get Started</em>, you acknowledge reading and accepting our{' '}
                <a
                    href={ExternalLinks.PrivacyPolicy}
                    target="_blank"
                    rel="noreferrer"
                    className={link}
                >
                    Privacy Policy
                </a>{' '}
                and{' '}
                <a
                    href={ExternalLinks.TermsOfUse}
                    target="_blank"
                    rel="noreferrer"
                    className={link}
                >
                    Terms of Use
                </a>
                .
            </div>
            <ButtonControls
                nextLabel="Get Started"
                containerClassName="w-full"
                nextButtonClassName="w-full"
            />
        </Form>
    );
};
